/**
 * The illustration that heads the Completion Step.
 *
 * The design (Figma 8705-8520) puts a full-bleed banner above the confirmation, and the artwork is
 * expected to change with the campaign a visitor arrived on — a partner's own imagery, a seasonal
 * push. Which conditions pick which image has not been agreed yet, so today there is one image and
 * everybody gets it.
 *
 * This stays a named module rather than a literal in the step, because the second image is a
 * marketing decision and this is where it will be made. What is deliberately *not* here is the
 * machinery for choosing between images: a rule interface, a `when` predicate and an empty rule
 * table were written before a single rule existed, and an abstraction with nothing in it is harder
 * to replace than the one-line lookup it stands in for — it fixes the shape of the answer before
 * anyone knows the question. The selector arrives with the conditions it serves.
 *
 * When it does, reading the campaign parameters to make that choice is consistent with ADR 0004:
 * this is *copy selection*, not product selection. The parameters may choose what a confirmation
 * looks like — they already choose the `partner-welcome` wording — and may never choose what is on
 * sale.
 */

export interface CompletionHeaderImage {
  src: string;
  /**
   * Empty on purpose for the default: it is a decorative banner sitting directly above a heading
   * that already says what happened, so announcing it would only repeat the card. A campaign image
   * that carries meaning of its own — a partner's logo, say — should describe itself here.
   */
  alt: string;
}

/**
 * Served from the app's own asset host rather than bundled. The set is expected to grow with
 * campaigns that marketing runs on their own schedule, and uploading a file should not need a
 * deploy. `welcome-default.webp` is the Figma banner exported at 2x (812×312 for a 406×156 slot).
 */
const ASSET_BASE = 'https://app.zoolife.tv/assets/onboarding';

export const DEFAULT_COMPLETION_HEADER: CompletionHeaderImage = {
  src: `${ASSET_BASE}/welcome-default.webp`,
  alt: '',
};

export default DEFAULT_COMPLETION_HEADER;
